// Store callbacks outside window.smartform so they can't be overwritten
const smartformPendingCallbacks = [];
let smartformScriptLoading = false;

// Store country settings for each instance so they can be reapplied after rebindAllForms
const smartformCountrySettings = {};

// Store reinit callbacks for instances that may be unbound and need rebinding
const smartformReinitCallbacks = {};

// Map internal country IDs to SmartForm country codes (only SK and CZ are supported)
const SMARTFORM_COUNTRY_MAP = { 1: "SK", 56: "CZ" };

// Load validation messages from locale
const lang = document.querySelector("html")?.getAttribute("lang") || "sk";
let smartformTexts = {};

import(`Locale/${lang}.json`)
    .then((module) => {
        smartformTexts = module.Validate || {};
    })
    .catch(() => {
        console.warn("SmartForm: Could not load locale texts, using defaults");
    });

/**
 * Gets SmartForm instance for a given input element by checking the form
 * SmartForm creates one instance per form, not per input
 *
 * @param {HTMLElement} inputElement - The input element to get the instance for
 * @returns {Object|null} - SmartForm instance or null
 */
function getSmartFormInstanceForInput(inputElement) {
    if (!inputElement || !window.smartform || !window.smartform.getInstance) {
        return null;
    }

    const form = inputElement.closest("form");
    if (!form) {
        return null;
    }

    // Find all inputs with smartform-instance-* classes in the form
    const smartformInputs = form.querySelectorAll("input[class*='smartform-instance-']");

    for (const input of smartformInputs) {
        const instanceId = Array.from(input.classList).find((cls) =>
            cls.startsWith("smartform-instance-")
        );
        if (instanceId) {
            const instance = window.smartform.getInstance(instanceId);
            if (instance) {
                return instance;
            }
        }
    }

    return null;
}

/**
 * jQuery validator method for SmartForm email validation
 * Checks if email exists and is valid according to SmartForm
 */
jQuery.validator.addMethod(
    "smartformEmail",
    function (value, element, param) {
        const smartformInstance = getSmartFormInstanceForInput(element);

        if (!smartformInstance || !smartformInstance.emailControl) {
            return true;
        }

        // Get instance ID from element classes
        const instanceId = Array.from(element.classList).find((cls) =>
            cls.startsWith("smartform-instance-")
        );

        // First try our manually stored result
        let result = emailValidationResults[instanceId];

        // Fallback to SmartForm's method if we don't have a stored result
        if (!result) {
            result = smartformInstance.emailControl.getLastValidationResult();
        }

        const acceptableResults = ["EXISTS", "UNKNOWN"];

        if (!result) {
            return false;
        }

        // Check if we have the response structure from our callback
        if (result.emailValidationResponse) {
            const resultType = result.emailValidationResponse.resultType;
            return acceptableResults.includes(resultType);
        }

        // Handle SmartForm's getLastValidationResult() format
        if (result.result) {
            // If result is not up to date, schedule revalidation
            if (!result.upToDate) {
                const $element = $(element);
                const form = $element.parents("form:visible");

                // Schedule revalidation after a short delay to allow SmartForm to update
                setTimeout(() => {
                    if (form.length) {
                        const validator = form.data("validator");
                        if (validator) {
                            validator.element($element);
                        }
                    }
                }, 100);

                return acceptableResults.includes(result.result.resultType);
            }

            return result.upToDate && acceptableResults.includes(result.result.resultType);
        }

        return false;
    },
    jQuery.validator.format("Please enter a valid email address.")
);

/**
 * jQuery validator method for SmartForm phone validation
 * Checks if phone number is valid according to SmartForm
 */
jQuery.validator.addMethod(
    "smartformPhone",
    function (value, element, param) {
        const smartformInstance = getSmartFormInstanceForInput(element);

        if (!smartformInstance || !smartformInstance.phoneControl) {
            return true;
        }

        // Get instance ID from element classes
        const instanceId = Array.from(element.classList).find((cls) =>
            cls.startsWith("smartform-instance-")
        );

        // First try our manually stored result
        let result = phoneValidationResults[instanceId];

        // Fallback to SmartForm's method if we don't have a stored result
        if (!result) {
            result = smartformInstance.phoneControl.getLastValidationResult();
        }

        const acceptableResults = ["VALID", "UNKNOWN"];

        if (!result) {
            return false;
        }

        // Check if we have the response structure from our callback
        if (result.phoneValidationResponse) {
            const resultType = result.phoneValidationResponse.resultType;
            return acceptableResults.includes(resultType);
        }

        // Handle SmartForm's getLastValidationResult() format
        if (result.result) {
            // If result is not up to date, schedule revalidation
            if (!result.upToDate) {
                const $element = $(element);
                const form = $element.parents("form:visible");

                // Schedule revalidation after a short delay to allow SmartForm to update
                setTimeout(() => {
                    if (form.length) {
                        const validator = form.data("validator");
                        if (validator) {
                            validator.element($element);
                        }
                    }
                }, 100);

                return acceptableResults.includes(result.result.resultType);
            }

            return result.upToDate && acceptableResults.includes(result.result.resultType);
        }

        return false;
    },
    jQuery.validator.format("Please enter a valid phone number with country code.")
);

// Store validation results manually since getLastValidationResult() doesn't work reliably
const emailValidationResults = {};
const phoneValidationResults = {};

// Expose to window so vendor code can access it
window.emailValidationResults = emailValidationResults;
window.phoneValidationResults = phoneValidationResults;

/**
 * Email validation callback for SmartForm
 * Triggers jQuery validation when SmartForm email validation completes
 * Also dispatches custom DOM event for framework-agnostic integration (Vue, React, etc.)
 */
const emailValidationCallback = function (response, instanceId) {
    // Store the result manually
    emailValidationResults[instanceId] = response;

    const emailField = $("." + instanceId);
    const form = emailField.parents("form:visible");

    // jQuery Validate integration
    if (form.length && form.data("validator") !== undefined) {
        form.data("validator").element(emailField);
    }

    // Dispatch custom DOM event for Vue/React/vanilla JS integration
    const inputElement = document.querySelector("." + instanceId);
    if (inputElement) {
        // SmartForm returns result in response.result, not response.emailValidationResponse
        const resultType = response?.result?.resultType;
        const acceptableResults = ["EXISTS", "UNKNOWN"];
        const isValid = acceptableResults.includes(resultType);

        inputElement.dispatchEvent(
            new CustomEvent("smartform-email-validated", {
                bubbles: true,
                detail: {
                    valid: isValid,
                    resultType: resultType,
                    response: response,
                },
            })
        );
    }
};

/**
 * Phone validation callback for SmartForm
 * Triggers jQuery validation when SmartForm phone validation completes
 * Also dispatches custom DOM event for framework-agnostic integration (Vue, React, etc.)
 */
const phoneValidationCallback = function (response, instanceId) {
    // Store the result manually
    phoneValidationResults[instanceId] = response;

    const field = $("." + instanceId);
    const form = field.parents("form:visible");

    // jQuery Validate integration
    if (form.length && form.data("validator") !== undefined) {
        form.data("validator").element(field);
    }

    // Dispatch custom DOM event for Vue/React/vanilla JS integration
    const inputElement = document.querySelector("." + instanceId);
    if (inputElement) {
        // SmartForm returns result in response.result, not response.phoneValidationResponse
        const resultType = response?.result?.resultType;
        const acceptableResults = ["VALID", "UNKNOWN"];
        const isValid = acceptableResults.includes(resultType);

        inputElement.dispatchEvent(
            new CustomEvent("smartform-phone-validated", {
                bubbles: true,
                detail: {
                    valid: isValid,
                    resultType: resultType,
                    response: response,
                },
            })
        );
    }
};

/**
 * Initializes or validates a SmartForm instance (address autocomplete service)
 *
 * This function handles three scenarios:
 * 1. SmartForm not loaded yet - dynamically loads the script from CDN
 * 2. SmartForm loaded but instance invalid - rebinds all forms
 * 3. SmartForm loaded and instance valid - proceeds immediately
 *
 * @param {string} instanceId - CSS class name identifying the SmartForm input element
 * @param {Function} callback - Function to call after SmartForm is ready
 */
function initSmartForm(instanceId, callback = () => {}) {
    // Check if the target element exists on the page before proceeding
    const inputElement = document.querySelector("." + instanceId);
    if (!inputElement) {
        // Element doesn't exist on this page - silently skip (this is expected behavior)
        return;
    }

    const code = "aSC1GbESbc"; // SmartForm client ID for this application

    // Check if SmartForm library is already loaded in the browser
    if (window.smartform && window.smartform.getInstance) {
        // SmartForm is loaded - verify if this specific instance is valid
        let smartformInstance = window.smartform.getInstance(instanceId);

        // Check if SmartForm has already bound to this element (indicated by gwt-SuggestBox class)
        const hasSmartFormClass = inputElement.classList.contains("gwt-SuggestBox");

        // Instance is valid only if: it exists, element is in document, and has binding class
        const instanceValid =
            smartformInstance && document.contains(inputElement) && hasSmartFormClass;

        // If instance is invalid (element removed/re-rendered), rebind SmartForm to all forms
        if (!instanceValid && window.smartform.rebindAllForms) {
            window.smartform.rebindAllForms();

            // Wait 100ms for rebinding to complete, then reapply country settings and call callback
            setTimeout(() => {
                reapplyAllCountrySettings();
                callback();
            }, 100);
        } else {
            // Instance is valid, call callback immediately (with small delay for consistency)
            setTimeout(callback, 100);
        }
        return;
    }

    // SmartForm library not loaded yet - load it dynamically
    // Store callback for this instance
    smartformPendingCallbacks.push({ instanceId, callback });

    // Only load the script once
    if (smartformScriptLoading) {
        return;
    }
    smartformScriptLoading = true;

    var scriptTag = document.createElement("script");

    // Initialize SmartForm configuration object
    window.smartform = window.smartform || {};

    // Set client ID before SmartForm initialization
    window.smartform.beforeInit = function () {
        window.smartform.setClientId(code);
    };

    // Set callback to run after SmartForm initialization completes
    // Process all pending callbacks
    window.smartform.afterInit = function () {
        const callbacks = [...smartformPendingCallbacks]; // Copy array
        smartformPendingCallbacks.length = 0; // Clear the array

        callbacks.forEach(({ instanceId: id, callback: cb }) => {
            setTimeout(() => cb(), 200);
        });
    };

    // Load SmartForm v2 script from CDN
    scriptTag.src = "https://client.smartform.cz/v2/smartform.js";
    scriptTag.async = true;

    document.head.appendChild(scriptTag);
}

/**
 * Sets up SmartForm address autocomplete with auto-fill for a specific group
 *
 * This function:
 * 1. Initializes SmartForm for the instance
 * 2. Sets the country for address validation (reads from country input if not specified)
 * 3. Adds validation callback to auto-fill address fields when user selects an address
 *
 * @param {string} instanceId - CSS class name of the SmartForm input element (e.g., "smartform-instance-billing-address")
 * @param {string} group - Group name used in data-smartform-target attributes (e.g., "billing", "shipping")
 * @param {number|null} country - Country ID (1 = Slovakia, 56 = Czech Republic). If null, reads from country input field.
 */
function initSmartFormAddressAutofill(instanceId, group, country = null) {
    // Define the autofill callback as a named function so it can be re-added after reinit
    const autofillCallback = function (result) {
        const validationResult = result.result;

        // Check if validation returned a successful match (HIT)
        if (validationResult.type === window.smartform.AddressValidationResultType.HIT) {
            const addressArray = validationResult.addressArray;

            if (addressArray && addressArray[0]) {
                // Find target elements using data-smartform-target attributes
                const addressElement = document.querySelector(
                    `[data-smartform-target="${group}-address"]`
                );
                const cityElement = document.querySelector(
                    `[data-smartform-target="${group}-city"]`
                );
                const postCodeElement = document.querySelector(
                    `[data-smartform-target="${group}-post-code"]`
                );

                // Only auto-fill if all target elements exist
                if (addressElement && cityElement && postCodeElement) {
                    // Auto-fill street address (street name + house number)
                    addressElement.value =
                        addressArray[0].STREET + " " + addressArray[0].WHOLE_NUMBER;

                    // Auto-fill city
                    cityElement.value = addressArray[0].CITY;

                    // Auto-fill postal code
                    postCodeElement.value = addressArray[0].ZIP;

                    // Blur active element and trigger jQuery validation on filled fields
                    if (document.activeElement) {
                        document.activeElement.blur();
                    }

                    // Trigger validation on all filled fields
                    const form = $(addressElement).closest("form");
                    if (form.length && form.data("validator")) {
                        const validator = form.data("validator");
                        validator.element($(addressElement));
                        validator.element($(cityElement));
                        validator.element($(postCodeElement));
                    }
                }
            }
        }
    };

    // Setup function that configures a freshly-bound instance
    const setupInstance = (resolvedCountry) => {
        const smartformInstance = window.smartform.getInstance(instanceId);
        if (!smartformInstance || !smartformInstance.addressControl) {
            console.warn(
                `SmartForm: Instance "${instanceId}" not found or has no addressControl. Skipping address autofill setup.`
            );
            return;
        }

        smartformCountrySettings[instanceId] = resolvedCountry;

        if (SMARTFORM_COUNTRY_MAP[resolvedCountry]) {
            smartformInstance.addressControl.setAllSuggestionsEnabled(true);
            smartformInstance.addressControl.setCountry(SMARTFORM_COUNTRY_MAP[resolvedCountry]);
        }

        smartformInstance.addressControl.addValidationCallback(autofillCallback);
    };

    // Store reinit callback for rebinding after unbind (country change)
    smartformReinitCallbacks[instanceId] = (overrideCountry) => {
        initSmartForm(instanceId, function () {
            let resolvedCountry = overrideCountry !== undefined ? overrideCountry : country;
            if (resolvedCountry === null) {
                const countryElement = document.querySelector(
                    `[data-smartform-target="${group}-country"]`
                );
                resolvedCountry = countryElement ? parseInt(countryElement.value) : 1;
            }
            setupInstance(resolvedCountry);
        });
    };

    // Initial setup
    initSmartForm(instanceId, function () {
        // If country not specified, try to read from country input field
        if (country === null) {
            const countryElement = document.querySelector(
                `[data-smartform-target="${group}-country"]`
            );
            country = countryElement ? parseInt(countryElement.value) : 1; // Default to Slovakia if not found
        }

        // Set country for address validation (SK or CZ)
        setCountryForSmartformInstance(instanceId, country);

        // Add validation callback that fires when user selects an address
        const smartformInstance = window.smartform.getInstance(instanceId);
        if (smartformInstance?.addressControl) {
            smartformInstance.addressControl.addValidationCallback(autofillCallback);
        }
    });
}

/**
 * Sets up SmartForm city-only autocomplete
 *
 * This function initializes SmartForm for city autocomplete (using smartform-address-city class)
 * and sets the correct country for suggestions.
 *
 * @param {string} instanceId - CSS class name of the SmartForm input element (e.g., "smartform-instance-billing-city")
 * @param {string} group - Group name used in data-smartform-target attributes (e.g., "billing", "shipping")
 * @param {number|null} country - Country ID (1 = Slovakia, 56 = Czech Republic). If null, reads from country input field.
 */
function initSmartFormCityAutocomplete(instanceId, group, country = null) {
    initSmartForm(instanceId, function () {
        // If country not specified, try to read from country input field
        if (country === null) {
            const countryElement = document.querySelector(
                `[data-smartform-target="${group}-country"]`
            );
            country = countryElement ? parseInt(countryElement.value) : 1; // Default to Slovakia if not found
        }

        // Set country for city suggestions
        setCountryForSmartformInstance(instanceId, country);
    });
}

/**
 * Sets up SmartForm post code (ZIP) autocomplete for a specific field
 *
 * This function initializes SmartForm for post code autocomplete (using smartform-address-zip class)
 * and sets the correct country for suggestions.
 *
 * @param {string} instanceId - CSS class name of the SmartForm input element (e.g., "smartform-instance-billing-post-code")
 * @param {string} group - Group name used in data-smartform-target attributes (e.g., "billing", "shipping")
 * @param {number|null} country - Country ID (1 = Slovakia, 56 = Czech Republic). If null, reads from country input field.
 */
function initSmartFormPostCodeAutocomplete(instanceId, group, country = null) {
    initSmartForm(instanceId, function () {
        // If country not specified, try to read from country input field
        if (country === null) {
            const countryElement = document.querySelector(
                `[data-smartform-target="${group}-country"]`
            );
            country = countryElement ? parseInt(countryElement.value) : 1; // Default to Slovakia if not found
        }

        // Set country for post code suggestions
        setCountryForSmartformInstance(instanceId, country);
    });
}

/**
 * Detects country from the matching country input field using data-smartform-target convention.
 *
 * E.g., for an input with data-smartform-target="billing-post-code", looks for
 * an element with data-smartform-target="billing-country" and reads its value.
 *
 * @param {HTMLElement} input - The SmartForm input element
 * @returns {number} Country ID (defaults to 1 = Slovakia if not found)
 */
function detectCountryFromInput(input) {
    const target = input.getAttribute("data-smartform-target");
    if (target) {
        // Extract group by removing known suffixes: "billing-post-code" → "billing"
        const group = target.replace(/-(address|city|post-code|country)$/, "");
        const countryElement = document.querySelector(`[data-smartform-target="${group}-country"]`);
        if (countryElement) {
            return parseInt(countryElement.value) || 1;
        }
    }
    return 1; // Default to Slovakia
}

/**
 * Initializes SmartForm address autocomplete (with autofill) for ALL .smartform-address-whole-address fields
 *
 * Each field must have a unique smartform-instance-* class and a data-smartform-target attribute.
 * The group is extracted from the target: e.g. "billing-address" → group "billing".
 * Country is auto-detected from the matching data-smartform-target country input.
 */
function initAllSmartFormAddresses() {
    document.querySelectorAll(".smartform-address-whole-address").forEach((input) => {
        const instanceId = Array.from(input.classList).find((cls) =>
            cls.startsWith("smartform-instance-")
        );
        const target = input.getAttribute("data-smartform-target");

        if (instanceId && target) {
            // Extract group: "billing-address" → "billing", "customer-0-address" → "customer-0"
            const group = target.replace(/-address$/, "");
            initSmartFormAddressAutofill(instanceId, group);
        }
    });
}

/**
 * Initializes SmartForm post code autocomplete for ALL .smartform-address-zip fields
 *
 * Each field must have a unique smartform-instance-* class.
 * Country is auto-detected from the matching data-smartform-target country input.
 * Falls back to Slovakia (1) if no country input is found.
 */
function initAllSmartFormPostCodes() {
    document.querySelectorAll(".smartform-address-zip").forEach((input) => {
        const instanceId = Array.from(input.classList).find((cls) =>
            cls.startsWith("smartform-instance-")
        );

        if (instanceId) {
            // Store reinit callback for rebinding after unbind (country change)
            smartformReinitCallbacks[instanceId] = (overrideCountry) => {
                initSmartForm(instanceId, function () {
                    const country =
                        overrideCountry !== undefined
                            ? overrideCountry
                            : detectCountryFromInput(input);
                    smartformCountrySettings[instanceId] = country;
                    const si = window.smartform.getInstance(instanceId);
                    if (si?.addressControl && SMARTFORM_COUNTRY_MAP[country]) {
                        si.addressControl.setAllSuggestionsEnabled(true);
                        si.addressControl.setCountry(SMARTFORM_COUNTRY_MAP[country]);
                    }
                });
            };

            // Handle paste - clear jQuery Validate cache and re-validate
            input.addEventListener("paste", function () {
                const el = this;
                setTimeout(() => {
                    const $field = $(el);
                    const $form = $field.closest("form");
                    const validator = $form.data("validator");

                    if (validator) {
                        // Clear jQuery Validate's remote cache for this field
                        // by resetting previousValue
                        const previousValue = validator.previousValue(el);
                        if (previousValue) {
                            previousValue.old = null;
                            previousValue.valid = true;
                        }
                        // Trigger validation
                        validator.element($field);
                    }
                }, 100);
            });

            initSmartForm(instanceId, function () {
                const country = detectCountryFromInput(input);
                setCountryForSmartformInstance(instanceId, country);
            });
        }
    });
}

/**
 * Initializes SmartForm city autocomplete for ALL .smartform-address-city fields
 *
 * Each field must have a unique smartform-instance-* class.
 * Country is auto-detected from the matching data-smartform-target country input.
 * Falls back to Slovakia (1) if no country input is found.
 */
function initAllSmartFormCities() {
    document.querySelectorAll(".smartform-address-city").forEach((input) => {
        const instanceId = Array.from(input.classList).find((cls) =>
            cls.startsWith("smartform-instance-")
        );

        if (instanceId) {
            // Store reinit callback for rebinding after unbind (country change)
            smartformReinitCallbacks[instanceId] = (overrideCountry) => {
                initSmartForm(instanceId, function () {
                    const country =
                        overrideCountry !== undefined
                            ? overrideCountry
                            : detectCountryFromInput(input);
                    smartformCountrySettings[instanceId] = country;
                    const si = window.smartform.getInstance(instanceId);
                    if (si?.addressControl && SMARTFORM_COUNTRY_MAP[country]) {
                        si.addressControl.setAllSuggestionsEnabled(true);
                        si.addressControl.setCountry(SMARTFORM_COUNTRY_MAP[country]);
                    }
                });
            };

            // Handle paste - clear jQuery Validate cache and re-validate
            input.addEventListener("paste", function () {
                const el = this;
                setTimeout(() => {
                    const $field = $(el);
                    const $form = $field.closest("form");
                    const validator = $form.data("validator");

                    if (validator) {
                        // Clear jQuery Validate's remote cache for this field
                        // by resetting previousValue
                        const previousValue = validator.previousValue(el);
                        if (previousValue) {
                            previousValue.old = null;
                            previousValue.valid = true;
                        }
                        // Trigger validation
                        validator.element($field);
                    }
                }, 100);
            });

            initSmartForm(instanceId, function () {
                const country = detectCountryFromInput(input);
                setCountryForSmartformInstance(instanceId, country);
            });
        }
    });
}

/**
 * Sets the country for SmartForm address validation
 *
 * SmartForm only supports Slovakia and Czech Republic. For other countries,
 * SmartForm is fully unbound from the input to stop all outgoing requests.
 * When switching back to a supported country, the instance is reinitialized
 * via stored reinit callbacks.
 *
 * @param {string} instanceId - CSS class name of the SmartForm input element
 * @param {number} country - Country ID (1 = Slovakia, 56 = Czech Republic, others = unsupported)
 */
function setCountryForSmartformInstance(instanceId, country) {
    // Store the country setting so it can be reapplied after rebindAllForms
    smartformCountrySettings[instanceId] = country;

    const inputElement = document.querySelector("." + instanceId);
    if (!inputElement) return;

    const isBound = inputElement.classList.contains("gwt-SuggestBox");

    if (SMARTFORM_COUNTRY_MAP[country] === undefined) {
        // Unsupported country - fully unbind SmartForm to stop all requests
        if (isBound && window.smartform?.getInstance(instanceId)) {
            window.smartform.unbindForm(instanceId);
            inputElement.classList.remove("gwt-SuggestBox");
        }
        return;
    }

    // Supported country
    if (isBound) {
        // Already bound - just update country
        const smartformInstance = window.smartform.getInstance(instanceId);
        if (smartformInstance?.addressControl) {
            smartformInstance.addressControl.setAllSuggestionsEnabled(true);
            smartformInstance.addressControl.setCountry(SMARTFORM_COUNTRY_MAP[country]);
        }
    } else if (smartformReinitCallbacks[instanceId]) {
        // Was previously unbound - reinitialize via stored callback
        smartformReinitCallbacks[instanceId](country);
    }
}

/**
 * Reapplies all stored country settings after rebindAllForms
 * This is needed because rebindAllForms resets all instances to default country.
 * Also unbinds instances for unsupported countries (in case rebindAllForms rebound them).
 */
function reapplyAllCountrySettings() {
    Object.keys(smartformCountrySettings).forEach((instanceId) => {
        const country = smartformCountrySettings[instanceId];
        const inputElement = document.querySelector("." + instanceId);
        if (!inputElement) return;

        const isBound = inputElement.classList.contains("gwt-SuggestBox");

        if (SMARTFORM_COUNTRY_MAP[country]) {
            // Supported country - set it
            const smartformInstance = window.smartform.getInstance(instanceId);
            if (smartformInstance?.addressControl) {
                smartformInstance.addressControl.setAllSuggestionsEnabled(true);
                smartformInstance.addressControl.setCountry(SMARTFORM_COUNTRY_MAP[country]);
            }
        } else if (isBound) {
            // Unsupported country but instance was rebound - unbind it again
            if (window.smartform?.getInstance(instanceId)) {
                window.smartform.unbindForm(instanceId);
                inputElement.classList.remove("gwt-SuggestBox");
            }
        }
    });
}

/**
 * Completely unbinds and destroys a SmartForm instance
 *
 * This removes all SmartForm event listeners and data associated with the instance.
 * Use this when permanently removing a form or when you need a complete cleanup.
 *
 * @param {string} instanceId - CSS class name of the SmartForm input element
 */
function destroySmartform(instanceId) {
    window.smartform.unbindForm(instanceId);
}

/**
 * Cleans up SmartForm binding classes to prepare for re-initialization
 *
 * This function removes the 'gwt-SuggestBox' class that SmartForm adds when it binds
 * to an input element. This is useful in Vue.js/React components where the DOM element
 * might be re-rendered but the SmartForm instance persists, causing binding issues.
 *
 * Unlike destroySmartform(), this only removes the binding class without fully
 * unbinding the instance, allowing for a cleaner rebind operation.
 *
 * @param {string} instanceId - CSS class name of the SmartForm input element
 */
function cleanupSmartFormInstance(instanceId) {
    // Check if SmartForm is loaded and the instance exists
    if (window.smartform && window.smartform.getInstance) {
        const instance = window.smartform.getInstance(instanceId);
        if (instance) {
            // Find the input element by its class name
            const inputElement = document.querySelector("." + instanceId);
            if (inputElement) {
                // Remove SmartForm's binding class (gwt-SuggestBox)
                // This forces SmartForm to rebind when initSmartForm is called again
                inputElement.classList.remove("gwt-SuggestBox");
            }
        }
    }
}

/**
 * Initializes SmartForm email validation for all .smartform-email elements
 *
 * @param {Function} validationCallback - Callback function to call when email is validated (defaults to emailValidationCallback)
 * @param {string} validationMessage - Optional custom error message (uses locale text by default)
 */
function initAllSmartFormEmails(
    validationCallback = emailValidationCallback,
    validationMessage = null
) {
    document.querySelectorAll(".smartform-email").forEach((emailInput) => {
        const instanceId = Array.from(emailInput.classList).find((cls) =>
            cls.startsWith("smartform-instance-")
        );

        if (instanceId) {
            initSmartForm(instanceId, function () {
                const smartformInstance = window.smartform.getInstance(instanceId);

                if (smartformInstance && smartformInstance.emailControl) {
                    // Add callback to trigger jQuery validation when SmartForm validates
                    smartformInstance.emailControl.addValidationCallback(function (result) {
                        validationCallback(result, instanceId);
                    });

                    // Add jQuery validation rules
                    setTimeout(function () {
                        const $field = $("." + instanceId);
                        const form = $field.closest("form");

                        if (form.data("validator") !== undefined) {
                            $field.rules("add", {
                                smartformEmail: true,
                                messages: {
                                    smartformEmail:
                                        validationMessage ||
                                        smartformTexts.smartformEmail ||
                                        "Please enter a valid email address.",
                                },
                            });
                        }
                    }, 500);
                }
            });
        }
    });
}

/**
 * Initializes SmartForm phone validation for all .smartform-phone-number elements
 *
 * @param {Function} validationCallback - Callback function to call when phone is validated (defaults to phoneValidationCallback)
 * @param {string} validationMessage - Optional custom error message (uses locale text by default)
 */
function initAllSmartFormPhones(
    validationCallback = phoneValidationCallback,
    validationMessage = null
) {
    document.querySelectorAll(".smartform-phone-number").forEach((phoneInput) => {
        const instanceId = Array.from(phoneInput.classList).find((cls) =>
            cls.startsWith("smartform-instance-")
        );

        if (instanceId) {
            const phoneElement = document.querySelector("." + instanceId);
            if (!phoneElement) {
                console.warn(`Phone element with class ${instanceId} not found`);
                return;
            }

            initSmartForm(instanceId, function () {
                // Get SmartForm instance directly using the phone's instance ID
                const smartformInstance = window.smartform.getInstance(instanceId);

                if (smartformInstance && smartformInstance.phoneControl) {
                    // Get locale from form data-locale attribute
                    const $field = $("." + instanceId);
                    const dataLocale = $field.parents("form").attr("data-locale");
                    const locale = dataLocale ? dataLocale.split("_")[1] : "SK";

                    // Set phone control settings
                    smartformInstance.phoneControl.setDefaultPrefix(locale);
                    smartformInstance.phoneControl.setAutomaticFormattingFormat("INTERNATIONAL");

                    // Enable initial validation for phone control
                    smartformInstance.phoneControl.setInitialValidationEnabled(true);

                    // Add callback to trigger jQuery validation when SmartForm validates
                    smartformInstance.phoneControl.addValidationCallback(function (result) {
                        validationCallback(result, instanceId);
                    });

                    // Add jQuery validation rules
                    setTimeout(function () {
                        const form = $field.closest("form");

                        if (form.data("validator") !== undefined) {
                            $field.rules("add", {
                                smartformPhone: true,
                                messages: {
                                    smartformPhone:
                                        validationMessage ||
                                        smartformTexts.smartformPhone ||
                                        "Please enter a valid phone number with country code.",
                                },
                            });
                        }
                    }, 500);
                }
            });
        }
    });
}

/**
 * Initializes SmartForm email and phone validation
 * Call this once to enable validation for all email and phone fields
 */
function initSmartFormValidation() {
    initAllSmartFormEmails();
    initAllSmartFormPhones();
}

/**
 * Adds change listeners to all country selects with data-smartform-target="*-country".
 * When the country changes, all SmartForm instances in the same group are updated.
 * Supported countries (SK=1, CZ=56) enable autocomplete; others disable it.
 */
function initSmartFormCountryListeners() {
    document.querySelectorAll("[data-smartform-target$='-country']").forEach((countrySelect) => {
        countrySelect.addEventListener("change", function () {
            const target = this.getAttribute("data-smartform-target");
            // "billing-country" → "billing"
            const group = target.replace(/-country$/, "");
            const countryId = parseInt(this.value) || 0;

            // Find all SmartForm inputs in the same group
            const groupTargets = ["-address", "-city", "-post-code"];
            groupTargets.forEach((suffix) => {
                const el = document.querySelector(`[data-smartform-target="${group}${suffix}"]`);
                if (el) {
                    const instanceId = Array.from(el.classList).find((cls) =>
                        cls.startsWith("smartform-instance-")
                    );
                    if (instanceId) {
                        setCountryForSmartformInstance(instanceId, countryId);
                    }
                }
            });
        });
    });
}

export {
    initSmartForm,
    initSmartFormAddressAutofill,
    initSmartFormCityAutocomplete,
    initSmartFormPostCodeAutocomplete,
    initAllSmartFormAddresses,
    initAllSmartFormCities,
    initAllSmartFormPostCodes,
    initAllSmartFormEmails,
    initAllSmartFormPhones,
    initSmartFormValidation,
    setCountryForSmartformInstance,
    initSmartFormCountryListeners,
    destroySmartform,
    cleanupSmartFormInstance,
};
